import { useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import {
  DialogActions,
  DialogContent,
  Button,
  Table,
  TableRow,
  TableCell,
  Dialog,
  TableBody,
  RadioGroup,
  Radio,
  FormControlLabel,
  DialogTitle,
  TableHead,
  Divider,
  Paper
} from "@mui/material";
import Checkbox from "../../../form/controls/Checkbox";
import { closeCopyOrderDialog } from "../../../../business/actions/applicationActions";
import {
  CREATE_PAGE,
  PLACEHOLDER
} from "../../../../configurations/appConstants";
import { Grid } from "../styles/divideOrdersDialog";
import AutoComplete from "../../../form/controls/AutoComplete";
import { getOrders } from "../../../../business/models/Order/orderSelectors";
import { Typography } from "../styles/planExportDialog";
import { solitude } from "../../../../assets/styles/colors";
import {
  ORDER_CHECKED_PROPERTY,
  ORDER_END_DATE_ERROR_TEXT_PROPERTY,
  ORDER_END_DATE_PROPERTY,
  ORDER_START_DATE_ERROR_TEXT_PROPERTY,
  ORDER_START_DATE_PROPERTY
} from "../../../../business/models/Order/orderConstants";
import { validation } from "../../../../business/models/common/utils/validationUtils";
import {
  generateUrlPath,
  parseDate2
} from "../../../../business/models/common/utils/clientUtils";
import {
  copyOrders,
  getCampaignPlansForCopyFunctionality,
  getCampaignsForCopyFunctionality
} from "../../../../business/models/Order/orderActions";
import { isNullOrUndefinedOrEmpty } from "../../../../functions/util";
import CopyOrderItem from "../../../panels/extensions/CopyOrderItem";

const CopyOrderDialog = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { agency } = useParams();
  const emptyObject = { items: [{ key: "", text: "" }] };
  const planOrders = useSelector(getOrders);
  const [
    {
      campaignId,
      planId,
      startDate,
      endDate,
      mediaTypeId,
      mediaTypeName,
      planAgreementId
    }
  ] =
    planOrders.length > 0
      ? planOrders
      : [
          {
            startDate: "",
            endDate: "",
            mediaTypeId: "",
            mediaTypeName: "",
            planAgreementId: ""
          }
        ];
  const [copyToSamePlan, setCopyToSamePlan] = useState(true);
  const [selectAllCheckboxValue, setSelectAllCheckboxValue] = useState(false);
  const [selectedCampaignId, setSelectedCampaignId] = useState(campaignId);
  const [selectedPlanId, setSelectedPlanId] = useState(planId);
  const [selectedPlanAgreementId, setSelectedPlanAgreementId] = useState(
    planAgreementId
  );
  const [selectedStartDate, setSelectedStartDate] = useState(startDate);
  const [selectedEndDate, setSelectedEndDate] = useState(endDate);
  const [currentOrders, setCurrentOrders] = useState(
    planOrders.map(x => {
      return {
        ...x,
        [ORDER_CHECKED_PROPERTY]: false,
        [ORDER_START_DATE_ERROR_TEXT_PROPERTY]: "",
        [ORDER_END_DATE_ERROR_TEXT_PROPERTY]: ""
      };
    })
  );
  const [campaignsForSelection, setCampaignsForSelection] = useState([]);
  const [plansForSelection, setPlansForSelection] = useState([]);
  const [campaignSelectionErrorText, setCampaignSelectionErrorText] = useState(
    ""
  );
  const [planSelectionErrorText, setPlanSelectionErrorText] = useState("");
  const [validationErrorText, setValidationErrorText] = useState("");

  const loadCampaigns = async () => {
    const query = {
      isAscending: true,
      sortbycolumn: "campaignName",
      page: 1,
      pagesize: 9999,
      searchmediatypes: mediaTypeName
    };
    const { items } =
      (await dispatch(getCampaignsForCopyFunctionality(query))) ?? emptyObject;

    setCampaignsForSelection(
      items.map(i => ({
        key: i.campaignId,
        text: `${i.marathonCampaignId} | ${i.campaignName}`,
        startDate: i.startDate,
        endDate: i.endDate
      }))
    );
  };

  const loadCampaignPlans = async campaignIdForPlan => {
    if (campaignIdForPlan) {
      const { items } =
        (await dispatch(
          getCampaignPlansForCopyFunctionality(campaignIdForPlan)
        )) ?? emptyObject;

      setPlansForSelection(
        items
          .filter(x => x.mediaTypeId === mediaTypeId)
          .map(i => ({
            key: i.planId,
            text: `${i.marathonPlanId} | ${i.planName}`,
            planAgreementId: i.planAgreementId
          }))
      );
    }
  };
  const updateCurrentOrdersArray = (index, value, property) => {
    return currentOrders.map(p => {
      if (isNullOrUndefinedOrEmpty(index) || p.marathonOrderId === index) {
        return {
          ...p,
          [property]: value,
          [ORDER_START_DATE_ERROR_TEXT_PROPERTY]: "",
          [ORDER_END_DATE_ERROR_TEXT_PROPERTY]: ""
        };
      }
      return p;
    });
  };

  const validateOrderDates = (value, property, comparisonDate) => {
    if (!validation.date(value ?? "*")) {
      return "* Date is mandatory";
    }

    if (value > selectedEndDate || value < selectedStartDate) {
      return `* ${
        property === ORDER_START_DATE_PROPERTY ? `Start` : `End`
      } date is out of Campaign date range`;
    }

    if (property === ORDER_START_DATE_PROPERTY && value > comparisonDate) {
      return "* Start date must be before End date";
    }

    if (property === ORDER_END_DATE_PROPERTY && value < comparisonDate) {
      return "* End date must be after Start date";
    }

    return "";
  };

  const validateSelectedOrders = () => {
    const validatedOrders = currentOrders.map(p => {
      if (p.checked) {
        return {
          ...p,
          [ORDER_START_DATE_ERROR_TEXT_PROPERTY]: validateOrderDates(
            p.orderStartDate,
            ORDER_START_DATE_PROPERTY,
            p.orderEndDate
          ),
          [ORDER_END_DATE_ERROR_TEXT_PROPERTY]: validateOrderDates(
            p.orderEndDate,
            ORDER_END_DATE_PROPERTY,
            p.orderStartDate
          )
        };
      }
      return p;
    });

    setCurrentOrders(validatedOrders);

    if (
      validatedOrders.some(
        item =>
          item[ORDER_START_DATE_ERROR_TEXT_PROPERTY].length > 0 ||
          item[ORDER_END_DATE_ERROR_TEXT_PROPERTY].length > 0
      )
    ) {
      return false;
    }
    return true;
  };

  const handleCopyToSamePlanChange = event => {
    const selectedCopyToSamePlan = event.target.value === "true";
    setSelectedStartDate(startDate);
    setSelectedEndDate(endDate);
    setCopyToSamePlan(selectedCopyToSamePlan);
    setSelectedCampaignId(campaignId);
    setSelectedPlanId(planId);
    setSelectedPlanAgreementId(planAgreementId);

    if (!selectedCopyToSamePlan) {
      setSelectedCampaignId();
      setSelectedPlanId();
      setSelectedPlanAgreementId();
      loadCampaigns();
      setSelectedStartDate(PLACEHOLDER);
      setSelectedEndDate(PLACEHOLDER);
    }
  };

  const handleCampaignSelectionChange = selectedValue => {
    const selectedCampaignDetails = campaignsForSelection.filter(
      x => x.key === selectedValue
    );
    const selectedCampaignStartDate =
      selectedCampaignDetails.length > 0
        ? selectedCampaignDetails[0].startDate
        : PLACEHOLDER;
    const selectedCampaignEndDate =
      selectedCampaignDetails.length > 0
        ? selectedCampaignDetails[0].endDate
        : PLACEHOLDER;

    setSelectedStartDate(selectedCampaignStartDate);
    setSelectedEndDate(selectedCampaignEndDate);
    setSelectedCampaignId(selectedValue);
    setSelectedPlanId();
    setSelectedPlanAgreementId();
    setCampaignSelectionErrorText("");
    setPlanSelectionErrorText("");
    loadCampaignPlans(selectedValue);
  };

  const handlePlanSelectionChange = selectedValue => {
    const selectedAgreementId = plansForSelection
      .filter(x => x.key === selectedValue)
      .map(y => y.planAgreementId)[0];
    setSelectedPlanId(selectedValue);
    setSelectedPlanAgreementId(selectedAgreementId);
    setCampaignSelectionErrorText("");
    setPlanSelectionErrorText("");
  };

  const handleValueChange = (index, value, property) => {
    const updatedOrderArray = updateCurrentOrdersArray(index, value, property);
    setCurrentOrders(updatedOrderArray);

    if (
      isNullOrUndefinedOrEmpty(index) &&
      property === ORDER_CHECKED_PROPERTY
    ) {
      setSelectAllCheckboxValue(value);
    } else {
      setSelectAllCheckboxValue(updatedOrderArray.every(next => next.checked));
    }
  };

  const handleCancel = () => {
    dispatch(closeCopyOrderDialog());
  };

  const handleOk = () => {
    setCampaignSelectionErrorText("");
    setPlanSelectionErrorText("");
    setValidationErrorText("");

    if (!copyToSamePlan) {
      // campaign selection validation
      if (isNullOrUndefinedOrEmpty(selectedCampaignId)) {
        setCampaignSelectionErrorText("* Campaign is mandatory");
        setPlanSelectionErrorText("* Plan is mandatory");
        return;
      }

      // plan selection validation
      if (isNullOrUndefinedOrEmpty(selectedPlanId)) {
        setPlanSelectionErrorText("* Plan is mandatory");
        return;
      }
    }

    // order selection validation
    if (!currentOrders.some(item => item.checked)) {
      setValidationErrorText("* Order selection is mandatory");
      return;
    }

    // start date and end date validation
    if (!validateSelectedOrders()) {
      return;
    }

    let copyOrderStatus = [];
    currentOrders
      .filter(x => x.checked)
      .map((obj, i, { length }) => {
        return dispatch(
          copyOrders(
            selectedPlanId,
            selectedPlanAgreementId,
            copyToSamePlan,
            obj
          )
        ).then(response => {
          copyOrderStatus.push(response);
          if (i + 1 === length) {
            if (!copyOrderStatus.some(x => x === undefined)) {
              dispatch(closeCopyOrderDialog()).then(() => {
                if (!copyToSamePlan) {
                  navigate(
                    `/${agency}/${CREATE_PAGE}${generateUrlPath(
                      selectedCampaignId,
                      selectedPlanId
                    )}`
                  );
                }
              });
            }
            copyOrderStatus = [];
          }
        });
      });
  };

  return (
    <Dialog fullWidth maxWidth="md" open PaperProps={{ square: true }}>
      <DialogTitle id="copy-orders-dialog-title">
        Copy Order(s) to Same or Different Plan
      </DialogTitle>
      <Divider></Divider>
      <DialogContent>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Paper sx={{ padding: 1 }} elevation={3}>
              <Typography variant="body1">
                Do you want to copy selected Order(s) to the same Plan?
              </Typography>
              <RadioGroup
                row
                name="radio-group-same-plan"
                value={copyToSamePlan}
                onChange={handleCopyToSamePlanChange}
              >
                <FormControlLabel
                  value={true}
                  control={<Radio />}
                  label="Yes"
                />
                <FormControlLabel
                  value={false}
                  control={<Radio />}
                  label="No"
                />
              </RadioGroup>
            </Paper>
          </Grid>
          <Grid item xs={12}>
            {!copyToSamePlan && (
              <Grid container spacing={3}>
                <Grid item xs={9}>
                  <Paper sx={{ padding: 1 }} elevation={3}>
                    <AutoComplete
                      id="autocomplete-campaign"
                      label="Select Campaign"
                      options={campaignsForSelection}
                      value={selectedCampaignId}
                      onChange={e => handleCampaignSelectionChange(e)}
                      errors={[campaignSelectionErrorText]}
                      displayErrors={campaignSelectionErrorText.length > 0}
                    ></AutoComplete>
                    <AutoComplete
                      id="autocomplete-campaign"
                      label="Select Plan"
                      options={plansForSelection}
                      value={selectedPlanId}
                      onChange={e => handlePlanSelectionChange(e)}
                      errors={[planSelectionErrorText]}
                      displayErrors={planSelectionErrorText.length > 0}
                    ></AutoComplete>
                  </Paper>
                </Grid>
                <Grid item xs={3}>
                  <Paper sx={{ padding: 1, backgroundColor: solitude }}>
                    <Grid container spacing={3}>
                      <Grid item xs={12} textAlign="center">
                        <Typography variant="subtitle2">
                          Campaign Start Date
                        </Typography>
                        <Typography variant="h6">
                          {selectedStartDate.length > 5
                            ? parseDate2(selectedStartDate)
                            : PLACEHOLDER}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} textAlign="center">
                        <Typography variant="subtitle2">
                          Campaign End Date
                        </Typography>
                        <Typography variant="h6">
                          {selectedEndDate.length > 5
                            ? parseDate2(selectedEndDate)
                            : PLACEHOLDER}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Paper>
                </Grid>
              </Grid>
            )}
          </Grid>
          {selectedStartDate.length > 5 && selectedEndDate.length > 5 && (
            <Grid item xs={12}>
              <Paper sx={{ padding: 1 }} elevation={3}>
                <Typography variant="body1">
                  Please select Order(s) to continue.
                </Typography>
                <Divider></Divider>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ padding: 0, width: 2 }}>
                        <Checkbox
                          id={`select-all-checkbox`}
                          onChange={e =>
                            handleValueChange(
                              undefined,
                              e,
                              ORDER_CHECKED_PROPERTY
                            )
                          }
                          value={selectAllCheckboxValue}
                        ></Checkbox>
                      </TableCell>
                      <TableCell sx={{ padding: 0 }}>Order Details</TableCell>
                      <TableCell sx={{ padding: 0 }}>Start Date</TableCell>
                      <TableCell sx={{ padding: 0.5 }}>End Date</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {currentOrders &&
                      currentOrders.map(item => {
                        return (
                          <CopyOrderItem
                            orderId={item.orderId}
                            marathonOrderId={item.marathonOrderId}
                            checked={item.checked}
                            description={item.description}
                            currencySupplierSymbol={item.currencySupplierSymbol}
                            netTotal={item.netTotal}
                            orderStartDate={item.orderStartDate}
                            orderStartDateErrorText={
                              item.orderStartDateErrorText
                            }
                            orderEndDate={item.orderEndDate}
                            orderEndDateErrorText={item.orderEndDateErrorText}
                            handleValueChange={handleValueChange}
                          ></CopyOrderItem>
                        );
                      })}
                  </TableBody>
                </Table>
              </Paper>
            </Grid>
          )}
          <Grid item xs={12}>
            <Typography
              color="error"
              variant="subtitle2"
              hidden={validationErrorText.length < 1}
            >
              {validationErrorText}
            </Typography>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button
          id="cancel"
          name="cancel"
          onClick={handleCancel}
          color="primary"
        >
          Cancel
        </Button>
        <Button id="create" name="create" onClick={handleOk} color="secondary">
          OK
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CopyOrderDialog;
